/**
 * User's browser local **wall-clock** time, tagged `+00:00` so the backend stores it verbatim
 * (no UTC conversion). The DB therefore holds local time, which `parseDbDateLocal` reads back as-is.
 * e.g. at 11:29 local → 2026-06-05T11:29:30.000+00:00 → stored 2026-06-05 11:29:30.
 */
export function localNowForDb(): string {
  const d = new Date();
  const pad = (n: number, len = 2) => String(n).padStart(len, '0');

  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}` +
    `+00:00`
  );
}

/**
 * A date-only value (`YYYY-MM-DD`, straight out of an `<input type="date">`) widened to the
 * date-time literal the API's `DateTime` scalar accepts — midnight, tagged `+00:00` so the day is
 * stored verbatim exactly as `localNowForDb` does.
 *
 * A bare `2026-08-26` is rejected outright: *"DateTime cannot parse the given literal of type
 * StringValueNode"*. It bites only on columns the POCO generator typed `DateTime?` — a sibling
 * like `Booking.BalanceDueDate` is `string?` and round-trips the short form happily — so the
 * shape of the value has to follow the POCO, not the SQL type.
 *
 * Returns `undefined` for an empty date so the field is omitted rather than sent as null.
 */
export function dateOnlyForDb(iso?: string | null): string | undefined {
  const day = (iso ?? '').trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? `${day}T00:00:00.000+00:00` : undefined;
}

/**
 * Yesterday's date as `YYYY-MM-DD` — the max selectable value for a Date-of-Birth `<input type="date">`
 * so the picker disallows today and future dates (a DOB must be in the past).
 */
export function maxBirthDateInput(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Parse a DB datetime string as LOCAL wall-clock time, ignoring any timezone
 * designator. Our timestamps store the user's local time (e.g. Malaysia), but
 * some come back as naive strings (no offset, space separator) or tagged 'Z' —
 * `new Date()` would then shift them by the local offset (15:47 → 23:47).
 * We read the Y-M-D H:M:S components literally and build a local Date instead.
 */
export function parseDbDateLocal(value?: string | null): Date | null {
  if (!value) return null;
  const m = String(value).match(/(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) {
    const fallback = new Date(value);
    return isNaN(fallback.getTime()) ? null : fallback;
  }
  const [, y, mo, d, h, mi, s] = m;
  return new Date(+y, +mo - 1, +d, +h, +mi, s ? +s : 0);
}
